(() => {
  'use strict';

  if (customElements.get('dieux-carousel')) return;

  class DieuxCarousel extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.responsiveMode = this.getResponsiveMode();
      this.page = undefined;
      this.pageCount = undefined;

      this.trackObserver?.disconnect();
      this.trackObserver = null;
      if (document.documentElement.classList.contains('shopify-design-mode')) {
        this.trackObserver = new MutationObserver(this.handleTrackChange);
        const track = this.own('[data-carousel-track]');
        if (track) this.trackObserver.observe(track, { childList: true });
      }

      window.addEventListener('dieux:swiper:ready', this.mountCarousel, { signal });

      this.metricsObserver = new ResizeObserver(this.scheduleMetrics);
      this.metricsObserver.observe(this);

      const viewport = this.own('[data-carousel-viewport]');
      if (viewport instanceof HTMLElement) {
        viewport.addEventListener('scroll', this.scheduleCounter, { signal, passive: true });
      }

      this.mountCarousel();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.trackObserver?.disconnect();
      this.trackObserver = null;
      this.metricsObserver?.disconnect();
      this.metricsObserver = null;
      cancelAnimationFrame(this.metricsFrame || 0);
      this.metricsFrame = 0;
      cancelAnimationFrame(this.counterFrame || 0);
      this.counterFrame = 0;
      cancelAnimationFrame(this.reinitializeFrame || 0);
      this.reinitializeFrame = 0;
      window.DieuxFrame?.cancelMeasure(this.anchorMeasureKey);
      this.carouselController?.destroy();
      this.carouselController = null;
    }

    get slideSelector() {
      return this.dataset.carouselSlideSelector || '.g-carousel__slide';
    }

    get isNested() {
      const owner = this.parentElement?.closest('dieux-carousel, block-sections-carousel');
      return owner instanceof HTMLElement;
    }

    own(selector) {
      const nodes = this.querySelectorAll(selector);
      for (const node of nodes) {
        if (node.closest('dieux-carousel') === this) return node;
      }
      return null;
    }

    ownAll(selector) {
      return [...this.querySelectorAll(selector)].filter(
        (node) => node.closest('dieux-carousel') === this
      );
    }

    getResponsiveMode() {
      const frame = this.own('[data-carousel-frame]');
      return (
        getComputedStyle(frame || this).getPropertyValue('--g-carousel-responsive-mode').trim()
        || 'rail'
      );
    }

    getGap() {
      const gap = Number.parseFloat(getComputedStyle(this).getPropertyValue('--g-carousel-gap'));
      return Number.isFinite(gap) ? gap : 0;
    }

    getColumnCount() {
      const styles = getComputedStyle(this);
      const columns = Number.parseFloat(styles.getPropertyValue('--g-carousel-columns'));
      const declared = Number.isFinite(columns) && columns >= 1 ? Math.round(columns) : 1;

      const minColumnWidth = Number.parseFloat(
        styles.getPropertyValue('--g-carousel-min-column-width')
      );
      if (!Number.isFinite(minColumnWidth) || minColumnWidth <= 0) return declared;

      const track = this.own('[data-carousel-track]');
      if (!(track instanceof HTMLElement) || track.clientWidth === 0) return declared;

      const gap = this.getGap();
      const fits = Math.floor((track.clientWidth + gap) / (minColumnWidth + gap));
      return Math.min(declared, Math.max(1, fits));
    }

    anchorMeasureKey = Symbol('carousel-anchor');

    scheduleMetrics = () => {
      if (this.metricsFrame) return;
      this.metricsFrame = requestAnimationFrame(() => {
        this.metricsFrame = 0;
        if (!this.isConnected) return;
        if (this.getResponsiveMode() !== this.responsiveMode) {
          this.mountCarousel();
          return;
        }
        this.carouselController?.refresh(false);
        this.updateCounter();
      });
    };

    scheduleCounter = () => {
      if (this.counterFrame) return;
      this.counterFrame = requestAnimationFrame(() => {
        this.counterFrame = 0;
        if (this.isConnected) this.updateCounter();
      });
    };

    handleTrackChange = () => {
      this.carouselController?.destroy();
      this.carouselController = null;
      if (this.reinitializeFrame) return;
      this.reinitializeFrame = requestAnimationFrame(() => {
        this.reinitializeFrame = 0;
        this.mountCarousel();
      });
    };

    mountCarousel = () => {
      if (!this.isConnected) return;
      this.responsiveMode = this.getResponsiveMode();
      this.carouselController?.destroy();
      this.carouselController = null;

      const viewport = this.own('[data-carousel-viewport]');
      if (this.responsiveMode === 'grid') {
        this.dataset.swiperState = 'static';
        if (viewport instanceof HTMLElement) viewport.scrollLeft = 0;
        this.updateCounter();
        return;
      }

      const track = this.own('[data-carousel-track]');
      if (
        !(viewport instanceof HTMLElement)
        || !(track instanceof HTMLElement)
        || !window.DieuxSwiper
      ) {
        this.updateCounter();
        return;
      }

      const loops = this.hasAttribute('data-carousel-loop');

      this.carouselController = window.DieuxSwiper.mount({
        owner: this,
        viewport,
        track,
        stateTarget: this,
        previousButton: this.own('[data-swiper-button="previous"]'),
        nextButton: this.own('[data-swiper-button="next"]'),
        unavailableControls:
          this.dataset.carouselUnavailableControls === 'disabled' ? 'disabled' : undefined,
        isNestedScroller: this.isNested,
        mousewheel: true,
        gapProperty: '--g-carousel-gap',
        gapSource: this,
        loopFill: loops,
        options: () => ({
          slidesPerView: 'auto',
          loop: loops && this.ownAll(`${this.slideSelector}:not([data-swiper-loop-clone])`).length > 1,
          a11y: {
            enabled: false,
          },
        }),
        onStateChange: ({ state, controller }) => {
          if (state === 'enhanced') {
            requestAnimationFrame(() => {
              controller.refresh();
              this.bindInstanceCounter(controller);
            });
          }
          this.updateCounter();
        },
      });

      this.updateCounter();
    };

    /**
     * The counter is a page count, not a slide count: a rail showing three of four tiles is on page one of two.
     */
    bindInstanceCounter(controller) {
      const instance = controller?.instance;
      if (!instance || instance.destroyed || instance.__dieuxCounterBound) return;
      instance.__dieuxCounterBound = true;
      instance.on('slideChange', this.scheduleCounter);
      instance.on('transitionEnd', this.scheduleCounter);
    }

    updateCounter() {
      const output = this.own('[data-carousel-count]');
      const status = this.own('[data-carousel-status]');
      const controls = this.own('[data-carousel-controls]');
      if (
        !(output instanceof HTMLElement)
        && !(status instanceof HTMLElement)
        && !(controls instanceof HTMLElement)
        && !this.querySelector('dieux-read-more')
      ) {
        return;
      }

      // Loop mode relocates real slides and can add fill clones, so the counter
      // has to count the owner's own set or it reports more positions than exist.
      const slides = this.ownAll(this.slideSelector)
        .filter((slide) => !slide.hasAttribute('data-swiper-loop-clone'));
      const columns = this.getColumnCount();
      const instance = this.carouselController?.instance;
      const live = Boolean(instance) && !instance.destroyed;
      const railPositions = Math.max(1, slides.length - Math.max(1, Math.floor(columns)) + 1);
      const loops = live
        ? instance.params?.loop === true
        : this.hasAttribute('data-carousel-loop') && railPositions > 1;
      const pageCount =
        this.getResponsiveMode() === 'grid'
          ? 1
          : loops
            ? Math.max(1, slides.length)
            : railPositions;

      let page = 1;
      if (instance && !instance.destroyed) {
        page = (loops ? instance.realIndex : instance.activeIndex) + 1;
      } else {
        const viewport = this.own('[data-carousel-viewport]');
        const first = slides[0];
        if (viewport instanceof HTMLElement && first instanceof HTMLElement) {
          const step = first.getBoundingClientRect().width + this.getGap();
          if (step > 0) page = Math.round(viewport.scrollLeft / step) + 1;
        }
      }
      page = Math.min(Math.max(page, 1), pageCount);
      const previousPage = this.page;
      const previousPageCount = this.pageCount;
      this.pageCount = pageCount;
      this.trackPage(page);

      if (controls instanceof HTMLElement) {
        controls.hidden = pageCount <= 1;
      }

      if (output instanceof HTMLElement) {
        output.textContent = `${page}/${pageCount}`;
      }
      if (status instanceof HTMLElement) {
        const moved = previousPage !== undefined && previousPage !== page;
        if (pageCount > 1 && moved && previousPageCount === pageCount) {
          const template = status.dataset.carouselStatusTemplate || '';
          status.textContent = template
            ? template.replace('[index]', String(page)).replace('[length]', String(pageCount))
            : `${page}/${pageCount}`;
        } else if (pageCount <= 1 || previousPageCount !== pageCount) {
          if (status.textContent !== '') status.textContent = '';
        }
      }
    }

    /**
     * Records the current page and collapses expanded read-mores when it moved.
     *
     * @param {number} page
     */
    trackPage(page) {
      const previous = this.page;
      this.page = page;
      if (previous === undefined || previous === page) return;
      this.collapseExpandedSlides();
    }

    /**
     * Collapses every read-more the rail carries at once, keeping the rail's top edge in view when the customer was scrolled into it.
     */
    collapseExpandedSlides() {
      const readMores = [...this.querySelectorAll('dieux-read-more')].filter(
        (readMore) => typeof readMore.collapse === 'function'
      );
      if (readMores.length === 0) return;

      const collapse = () => {
        let collapsed = false;
        for (const readMore of readMores) {
          if (readMore.collapse()) collapsed = true;
        }
        return collapsed;
      };

      const frame = window.DieuxFrame;
      if (!frame) {
        collapse();
        return;
      }

      frame.cancelMeasure(this.anchorMeasureKey);
      frame.measure(this.anchorMeasureKey, () => {
        if (!this.isConnected) return;
        const top = this.getBoundingClientRect().top;
        const offset = frame.resolveCustomProperty(this, '--header-height') || 0;
        const anchor = top < offset ? window.scrollY + top - offset : null;

        frame.write(() => {
          if (!this.isConnected) return;
          const collapsed = collapse();
          if (collapsed && anchor !== null) window.scrollTo({ top: anchor, behavior: 'instant' });
        });
      });
    }

    revealBlock(blockId, sourceElement) {
      if (!blockId || this.getResponsiveMode() === 'grid') return;

      const selector = this.slideSelector;
      const slides = this.ownAll(selector);
      const slide = sourceElement instanceof Element ? sourceElement.closest(selector) : null;
      if (!(slide instanceof HTMLElement) || !this.contains(slide)) return;

      const slideIndex = slides.indexOf(slide);

      const isInView = () => {
        const viewport = this.own('[data-carousel-viewport]');
        if (!(viewport instanceof HTMLElement)) return true;
        const slideBox = slide.getBoundingClientRect();
        const viewportBox = viewport.getBoundingClientRect();
        return slideBox.left >= viewportBox.left - 2 && slideBox.right <= viewportBox.right + 2;
      };

      const scrollToSlide = () => {
        const instance = this.carouselController?.instance;
        if (instance && !instance.destroyed && slideIndex >= 0) {
          instance.slideTo(slideIndex, 0);
        } else {
          const viewport = this.own('[data-carousel-viewport]');
          if (viewport instanceof HTMLElement) {
            viewport.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
          }
        }
        this.updateCounter();
      };

      const reveal = (attempt = 0) => {
        if (!this.isConnected) return;
        if (slide.getBoundingClientRect().width === 0) {
          if (attempt < 20) requestAnimationFrame(() => reveal(attempt + 1));
          return;
        }

        Promise.resolve(this.carouselController?.promote()).then(() => {
          if (!this.isConnected) return;
          scrollToSlide();
          if (attempt < 20) {
            requestAnimationFrame(() => {
              if (this.isConnected && !isInView()) reveal(attempt + 1);
            });
          }
        });
      };

      reveal();
    }
  }

  customElements.define('dieux-carousel', DieuxCarousel);
})();
